import { useState, useEffect, useCallback } from 'react'
import { Clock, AlertTriangle } from 'lucide-react'

interface TestTimerProps {
  durationMinutes: number
  startTime: Date
  onTimeUp: () => void
}

export default function TestTimer({ durationMinutes, startTime, onTimeUp }: TestTimerProps) {
  const [remainingMs, setRemainingMs] = useState(() => {
    const endTime = new Date(startTime.getTime() + durationMinutes * 60 * 1000)
    return Math.max(0, endTime.getTime() - Date.now())
  })

  const formatTime = useCallback((ms: number) => {
    const totalSeconds = Math.floor(ms / 1000)
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
    }
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
  }, [])

  useEffect(() => {
    const interval = setInterval(() => {
      const endTime = new Date(startTime.getTime() + durationMinutes * 60 * 1000)
      const remaining = Math.max(0, endTime.getTime() - Date.now())
      setRemainingMs(remaining)

      if (remaining <= 0) {
        clearInterval(interval)
        onTimeUp()
      }
    }, 1000)

    return () => clearInterval(interval)
  }, [durationMinutes, startTime, onTimeUp])

  const isLowTime = remainingMs < 5 * 60 * 1000 // Less than 5 minutes
  const isCriticalTime = remainingMs < 1 * 60 * 1000 // Less than 1 minute

  return (
    <div
      className={`flex items-center gap-2 px-3 py-1.5 rounded-lg font-mono text-sm font-semibold ${
        isCriticalTime
          ? 'bg-red-100 text-red-700 animate-pulse'
          : isLowTime
          ? 'bg-yellow-100 text-yellow-700'
          : 'bg-gray-100 text-gray-700'
      }`}
    >
      {isCriticalTime ? (
        <AlertTriangle className="w-4 h-4" />
      ) : (
        <Clock className="w-4 h-4" />
      )}
      <span>{formatTime(remainingMs)}</span>
    </div>
  )
}
